import { useCallback, useRef, useState } from "react";
import {
  buildPlanStarter,
  buildProgramFromPlanId,
  catalogBySlug,
  proposeTodaySession,
  resolveProgramSession,
  todayChainSets,
  weightedLastSets,
  type Exercise,
  type LastLoggedAnySet,
  type MaxForProposal,
  type PainMap,
  type Plan,
  type PlanStarter,
  type Program,
  type TodayGap,
} from "@heraion/shared";

import { useLocale } from "../primitives/useLocale";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import { lastLoggedSetsBeyondWindow } from "./lastLoggedBeyondWindow";
import { loadTodayHistory } from "./todayHistory";
import { ensureRecentWorkoutsCached } from "./useEntryHistory";

// THE "Start a workout" orchestration, for mobile and web-rnw
// (CN-workoutstarter-twin, closed with AP-9b). Plans, program sessions and
// the built «today» draft all resolve targets against the live MAX map,
// bodyweight and last-logged history into a PlanStarter, and hand it over.
// The three platform seams are injected, like useMaxesData's apiFetch: how
// the MAXes are read, how the bodyweight arrives, and the hand-off itself
// (mobile pushes to usePendingStart and navigates, web hands it to AppShell).

export type WorkoutStarterSeams = {
  apiFetch: ProgramApiFetch;
  catalog: ReadonlyArray<Exercise> | null;
  getMaxesDetailed: () => Promise<Map<string, MaxForProposal>>;
  getBodyweightKg: () => Promise<number | null>;
  onStarter: (starter: PlanStarter) => void;
};

export type StartBuiltSessionInput = {
  gaps: ReadonlyArray<TodayGap>;
  catalog: ReadonlyArray<Exercise>;
  painMap: PainMap | undefined;
  /** The athlete's rest (48 or 72 h). Absent: DEFAULT_REST_HOURS. */
  restHours?: number;
  /** «Build one anyway»: rest ignored, the most rested muscles first. */
  ignoreRest?: boolean;
};

// What a start hands buildPlanStarter, before the context.
type StartSpec = Omit<Parameters<typeof buildPlanStarter>[0], "ctx">;

export function useWorkoutStarter(seams: WorkoutStarterSeams) {
  const { apiFetch, catalog, getMaxesDetailed, getBodyweightKg, onStarter } = seams;
  const lang = useLocale();
  const [starting, setStarting] = useState<string | null>(null);
  // Ref guard so a double-tap can't fire two launches before the state
  // settles, without making `launch` depend on (and churn with) `starting`.
  const inFlight = useRef(false);

  const launch = useCallback(
    async (
      id: string,
      slugs: string[],
      build: (lastLogged: Map<string, LastLoggedAnySet>) => StartSpec | Promise<StartSpec>,
      // What a start may skip reading: the MAX map, and the last sets (the
      // 50-session window plus the per-exercise index behind it).
      // `hold`: hand the starter back instead of to the seam, so the caller can
      // swap it into a logger that is already open.
      opts: {
        catalogItems?: ReadonlyArray<Exercise>;
        skipMaxes?: true;
        skipWindow?: true;
        hold?: true;
      } = {},
    ): Promise<PlanStarter | undefined> => {
      if (inFlight.current) return undefined;
      inFlight.current = true;
      setStarting(id);
      try {
        // One MAX read, the detailed one: it carries value, source AND
        // `setAt`, and the chain needs all three (§17.5).
        const [maxesDetailed, bodyweightKg, recentWorkouts] = await Promise.all([
          opts.skipMaxes ? new Map<string, MaxForProposal>() : getMaxesDetailed(),
          getBodyweightKg(),
          opts.skipWindow ? [] : ensureRecentWorkoutsCached(apiFetch),
        ]);
        // The window first, then the per-exercise index for what it cannot
        // see (an exercise resumed after a long break).
        const lastLogged = opts.skipWindow
          ? new Map<string, LastLoggedAnySet>()
          : await lastLoggedSetsBeyondWindow(slugs, recentWorkouts, apiFetch);
        const spec = await build(lastLogged);
        const starter = buildPlanStarter({
          ...spec,
          ctx: {
            currentMaxes: maxesDetailed,
            bodyweightKg,
            // A built «today» draft reads its anchors, as its refits do
            // (useTodayDraft). A plan reads the weighted last sets.
            lastLoggedSets: spec.todayProposal
              ? todayChainSets(spec.todayProposal.rows)
              : weightedLastSets(lastLogged),
            catalogItems: [...(opts.catalogItems ?? catalog ?? [])],
          },
        });
        if (opts.hold) return starter;
        onStarter(starter);
        return undefined;
      } finally {
        inFlight.current = false;
        setStarting(null);
      }
    },
    [apiFetch, catalog, getMaxesDetailed, getBodyweightKg, onStarter],
  );

  const startPlan = useCallback(
    (p: Plan) =>
      launch(
        p.id,
        p.entries.map((e) => e.exerciseId),
        () => ({
          planId: p.id,
          name: p.name,
          ...(p.notes ? { notes: p.notes } : {}),
          entries: p.entries,
          // Single-day plans carry only a plan-level note (no program
          // description). Surface it read-only in the workout "?" panel.
          ...(p.notes ? { coachNotes: { sessionNote: p.notes } } : {}),
        }),
      ),
    [launch],
  );

  const startProgramSession = useCallback(
    (p: Program, weekIdx: number, sessionIdx: number) => {
      const resolved = resolveProgramSession({ program: p, weekIdx, sessionIdx, locale: lang });
      if (!resolved) return;
      // PE-21: the RESOLVED name, not the raw program field. This string is
      // persisted as the workout's name, so a per-locale object would have
      // been written to history as "[object Object]".
      const name = `${resolved.programName} · W${weekIdx + 1} · ${resolved.sessionName}`;
      // MC-8: the composite carries the stable session id (reorder-safe),
      // with the ordinal as a fallback rail.
      const planId = buildProgramFromPlanId(p.id, weekIdx, sessionIdx, resolved.sessionId);
      const coachNotes = {
        ...(resolved.programDescription
          ? { programDescription: resolved.programDescription }
          : {}),
        ...(resolved.sessionNotes ? { sessionNote: resolved.sessionNotes } : {}),
      };
      return launch(
        p.id,
        resolved.entries.map((e) => e.exerciseId),
        () => ({
          planId,
          name,
          entries: resolved.entries,
          ...(resolved.roundType ? { roundType: resolved.roundType } : {}),
          ...(Object.keys(coachNotes).length > 0 ? { coachNotes } : {}),
        }),
      );
    },
    [launch, lang],
  );

  // AP-9b: a draft from the AP-9 gaps. P0 57d74690: it waits for each
  // candidate's history (todayHistory.ts), not the window. No MAX: the kg is
  // the anchor's alone (D1, D2), as in the draft's refits.
  const buildToday = useCallback(
    (input: StartBuiltSessionInput, hold: boolean) =>
      launch(
        "today",
        [],
        async () => {
          const nowMs = Date.now();
          const slugs = [...new Set(input.gaps.flatMap((g) => g.candidates.map((c) => c.slug)))];
          const history = await loadTodayHistory({ slugs, catalog: input.catalog, apiFetch, nowMs });
          const { entries, view } = proposeTodaySession({
            gaps: input.gaps,
            catalog: catalogBySlug(input.catalog),
            painMap: input.painMap,
            history,
            nowMs,
            restHours: input.restHours,
            ignoreRest: input.ignoreRest,
          });
          return { entries, todayProposal: view };
        },
        { catalogItems: input.catalog, skipMaxes: true, skipWindow: true, ...(hold ? { hold: true as const } : {}) },
      ),
    [apiFetch, launch],
  );
  const startBuiltSession = useCallback(
    async (input: StartBuiltSessionInput): Promise<void> => {
      await buildToday(input, false);
    },
    [buildToday],
  );
  /** «Build one anyway»: the same draft as startBuiltSession, returned and not
   *  handed to the seam, for the open logger to swap in. */
  const buildTodayStarter = useCallback(
    (input: StartBuiltSessionInput) => buildToday(input, true),
    [buildToday],
  );

  return { starting, startPlan, startProgramSession, startBuiltSession, buildTodayStarter };
}
